import React, { useState, useRef, useEffect, useCallback } from "react";
import { useHistory, Link } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import InputAdornment from "@material-ui/core/InputAdornment";
import Icon from "@material-ui/core/Icon";
import Email from "@material-ui/icons/Email";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import CustomInput from "components/CustomInput/CustomInput.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import CardFooter from "components/Card/CardFooter.js";
import SimpleAlert from "../../components/AtutaAlerts/CustomAlerts";
import { useAuth } from "../../context/AuthContext";
import AuthApi from "../../api_calls/AuthApi";
import Configs from "../../utils/Configs";

import styles from "assets/jss/material-dashboard-pro-react/views/loginPageStyle.js";

const useStyles = makeStyles(styles);

// Turns the failure reason returned by AuthApi.signIn into a message that
// tells the user why they couldn't log in.
const loginFailureMessage = (data) => {
  switch (data?.message) {
    case "login_fail":
      return "Incorrect email or password. Please check them and try again.";
    case "account_suspended": {
      if (data.suspension_tag === "admin") {
        return "Your account has been suspended by an administrator. Please contact support.";
      }
      const days = Number(data.suspension_days);
      if (days > 0) {
        return `Your account has been suspended after repeated warnings. It will be reactivated in ${days} day${days === 1 ? "" : "s"}.`;
      }
      return "Your account has been suspended after repeated warnings. It will be reactivated shortly.";
    }
    case "network_error":
      return "We couldn't reach the server. Check your internet connection and try again.";
    default:
      return "Something went wrong on our side. Please try again in a moment.";
  }
};

export default function LoginPage() {
  const [cardAnimation, setCardAnimation] = useState("cardHidden");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({ email: "", password: "" });
  const [alertMessage, setAlertMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installAlertOpen, setInstallAlertOpen] = useState(false);

  const history = useHistory();
  const { storeUserData } = useAuth();
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    const id = setTimeout(() => {
      if (isMountedRef.current) setCardAnimation("");
    }, 300);
    return () => {
      isMountedRef.current = false;
      clearTimeout(id);
    };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      window.deferredPrompt = e;
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstallClick = useCallback(async () => {
    const promptEvent = deferredPrompt || window.deferredPrompt;
    if (promptEvent) {
      promptEvent.prompt();
      const { outcome } = await promptEvent.userChoice;
      window.deferredPrompt = null;
      setDeferredPrompt(null);
    } else {
      setInstallAlertOpen(true);
    }
  }, [deferredPrompt]);

  const classes = useStyles();
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError({ email: "", password: "" });
    setAlertMessage("");

    let valid = true;
    if (!email || !validateEmail(email)) {
      setError((prev) => ({ ...prev, email: "Please provide a valid email." }));
      valid = false;
    }
    if (!password) {
      setError((prev) => ({ ...prev, password: "Please provide a password." }));
      valid = false;
    }
    if (!valid) return;

    try {
      setLoading(true);
      const response = await AuthApi.signIn(email, password);
      if (!isMountedRef.current) return;

      if (response.status !== "fail") {
        const userData = response.data;
        const userPayload = {
          ...userData,
          message: userData?.message || response.message || "login_success",
        };

        storeUserData(userPayload);
        setAlertMessage("Login successful!");

        // ✅ ROLE-BASED ROUTING LOGIC
        // Redirect users with role 4 or 5 to their downlines page
        console.log("User Data after login:", userData);
        if (userData?.userrole === "4" || userData?.userrole === "5") {
          history.push(`/singles/downlines/${userData.user_id}`);
        } else {
          history.push("/singles/dashboard");
        }
      } else {
        setAlertMessage(loginFailureMessage(response.data));
      }
    } catch (err) {
      if (isMountedRef.current)
        setAlertMessage("Login failed. Please try again.");
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  return (
    <div className={classes.container}>
      <GridContainer justify="center">
        <GridItem xs={12} sm={6} md={6}>
          <form onSubmit={handleLogin}>
            <Card login className={classes[cardAnimation]}>
              <CardHeader
                className={`${classes.cardHeader} ${classes.textCenter}`}
                color="rose"
              >
                <h4 className={classes.cardTitle}>Beramour App Login</h4>
                <div className={classes.socialLine}>
                  <img
                    src="/logo-white.png"
                    alt="Logo"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "contain",
                    }}
                  />
                </div>
              </CardHeader>

              <CardBody>
                <div style={{ textAlign: "center", marginBottom: "20px" }}>
                  <div
                    style={{
                      color: "#e91e63",
                      fontWeight: "500",
                      fontSize: "13px",
                      marginBottom: "8px",
                    }}
                  >
                    Note: To install this app on your device, please use Chrome
                    or Brave browser.
                  </div>

                  <div
                    style={{
                      color: "#555",
                      fontSize: "14px",
                      lineHeight: "1.4",
                    }}
                  >
                    <style>
                      {`
                        @keyframes freePulse {
                          0% { transform: scale(1); box-shadow: 0 0 0 rgba(233,30,99,0.3); }
                          50% { transform: scale(1.08); box-shadow: 0 0 12px rgba(233,30,99,0.45); }
                          100% { transform: scale(1); box-shadow: 0 0 0 rgba(233,30,99,0.3); }
                        }

                        .free-pulse {
                          display: inline-block;
                          margin: 0 4px;
                          padding: 3px 10px;
                          border-radius: 20px;
                          background: rgba(233, 30, 99, 0.12);
                          border: 1px solid #e91e63;
                          color: #e91e63;
                          font-weight: 800;
                          letter-spacing: 0.5px;
                          animation: freePulse 1.6s ease-in-out infinite;
                        }
                      `}
                    </style>
                    Join Beramour today — enjoy full access{" "}
                    <strong className="free-pulse">FREE</strong> for a limited
                    time before our official paid launch (between $8 and $10 per
                    month). Terms and pricing will be communicated before
                    activation.
                  </div>
                </div>

                <CustomInput
                  labelText="Enter Email"
                  id="email"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Email className={classes.inputAdornmentIcon} />
                      </InputAdornment>
                    ),
                    value: email,
                    onChange: (e) => setEmail(e.target.value),
                    error: !!error.email,
                  }}
                />

                <CustomInput
                  labelText="Enter Password"
                  id="password"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Icon
                          className={classes.inputAdornmentIcon}
                          style={{ cursor: "pointer" }}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </Icon>
                      </InputAdornment>
                    ),
                    type: showPassword ? "text" : "password",
                    autoComplete: "off",
                    value: password,
                    onChange: (e) => setPassword(e.target.value),
                    error: !!error.password,
                  }}
                />

                <div style={{ textAlign: "right", marginTop: "10px" }}>
                  <Link
                    to="/auth/reset-password"
                    style={{ color: "#9c27b0", textDecoration: "none" }}
                  >
                    Forgot password?
                  </Link>
                </div>
              </CardBody>

              <CardFooter
                className={classes.justifyContentCenter}
                style={{ flexDirection: "column", alignItems: "center" }}
              >
                <Button color="rose" type="submit" disabled={loading}>
                  {loading ? "Logging in..." : "Proceed"}
                </Button>

                <p style={{ marginTop: "20px", textAlign: "left" }}>
                  Don't have an account?{" "}
                  <Link
                    to="/auth/register-page"
                    style={{ color: "#9c27b0", textDecoration: "none" }}
                  >
                    Create account
                  </Link>
                </p>

                <p
                  style={{
                    marginTop: "8px",
                    textAlign: "left",
                    fontSize: "13px",
                  }}
                >
                  Need help?{" "}
                  <a
                    href={Configs.supportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#9c27b0",
                      textDecoration: "none",
                      fontWeight: 500,
                    }}
                  >
                    Visit Support Center
                  </a>
                </p>
              </CardFooter>
            </Card>
          </form>

          {alertMessage && (
            <SimpleAlert
              message={alertMessage}
              onClose={() => setAlertMessage("")}
            />
          )}

          {window.deferredPrompt && (
            <Button
              color="rose"
              onClick={handleInstallClick}
              style={{ marginTop: "20px", width: "100%" }}
            >
              Install App
            </Button>
          )}

          {installAlertOpen && (
            <SimpleAlert
              message="To install the app, please use your browser's 'Add to Home Screen' or 'Install App' feature."
              onClose={() => setInstallAlertOpen(false)}
            />
          )}
        </GridItem>
      </GridContainer>
    </div>
  );
}
